import { useWarehouseLabels } from '../warehouseLabels';
import { AREA_TYPES } from './mapModel';

/**
 * Paleta do Editor de galpão. Dois gestos, um por natureza do elemento:
 *
 * - ÁREA: chip é FONTE DE ARRASTO — pressionar e arrastar até o canvas cria o
 *   retângulo default no ponto solto (ajustável pelas alças).
 * - RACK: chip é BOTÃO que ARMA o traçado — clicou, os próximos dois cliques
 *   no mapa são o início e o fim do corredor do rack (o gesto do protótipo do
 *   dono), e só então abre o painel de parâmetros. Rack tem comprimento e
 *   direção; arrastar um chip não sabe dizer isso, dois cliques sim.
 * - RACK SEM POSIÇÃO: racks que EXISTEM no cadastro (`wms_locations`) mas
 *   ainda não foram desenhados aparecem como chips próprios — clicar arma o
 *   mesmo traçado, só que sem gerar dado (os endereços já existem): é só o
 *   desenho. É o caminho para o galpão recadastrado do zero ganhar mapa.
 *
 * Layout em FAIXA horizontal (r5, item 5): a paleta vive ACIMA do canvas — de
 * coluna lateral ela roubava ~230px de largura do desenho, e o dono quer o
 * grid ocupando o espaço todo no modo edição.
 *
 * Os fills dos swatches são translúcidos (mesmos do canvas), então adaptam-se
 * sozinhos ao tema claro/escuro do chip por baixo.
 */

export const PALETTE_ITEMS = [
    ...Object.keys(AREA_TYPES).map((type) => ({ kind: 'area', type })),
    { kind: 'rack' },
];

/** Chave de rótulo do chip (wms.map.palette.{tipo|rack}). */
export function PaletteItemLabelKey(item) {
    return item.kind === 'rack' ? 'wms.map.palette.rack' : `wms.map.palette.${item.type}`;
}

const CHIP_BASE =
    'flex min-w-36 shrink-0 select-none items-center gap-2.5 rounded-lg border px-3 py-2 text-left ' +
    'text-sm font-medium shadow-sm-mono transition-colors';
const CHIP_IDLE =
    'border-mono-200 bg-white text-mono-700 hover:border-mono-400 hover:bg-mono-50 ' +
    'dark:border-mono-800 dark:bg-mono-900 dark:text-mono-300 dark:hover:border-mono-600 dark:hover:bg-mono-800';
const CHIP_ARMED =
    'border-link-500 bg-link-50 text-link-700 dark:border-link-500 dark:bg-link-900/30 dark:text-link-300';
const CHIP_UNPLACED =
    'border-dashed border-warning-400 bg-warning-soft text-warning-text hover:border-warning-600 ' +
    'dark:border-warning-700 dark:bg-warning-900/30 dark:text-warning-300';

const PaletteSwatch = ({ item }) => {
    if (item.kind === 'rack') {
        // Miniatura de rack: corredor cinza com a fileira de blocos de UM lado.
        return (
            <span
                aria-hidden="true"
                className="flex h-4 w-4 shrink-0 flex-col justify-between rounded-sm border border-mono-400 p-px dark:border-mono-500"
            >
                <span className="h-1.5 w-full rounded-sm bg-info-400/70" />
                <span className="h-1.5 w-full rounded-sm bg-mono-300 dark:bg-mono-600" />
            </span>
        );
    }
    const meta = AREA_TYPES[item.type] || AREA_TYPES.outro;
    return (
        <span
            aria-hidden="true"
            className="h-4 w-4 shrink-0 rounded-sm border-2"
            style={{ backgroundColor: meta.fill, borderColor: meta.stroke }}
        />
    );
};

/**
 * `onDragStart(item, event)` — pointerdown do chip de ÁREA; o WarehouseMap
 * assume o gesto (listeners de window) até o pointerup.
 * `onPickRack()` — clique no chip de RACK: arma o traçado de dois cliques.
 * `rackArmed` — traçado em curso (o chip fica marcado); `armedRack` = prefixo
 * do rack existente em posicionamento (marca o chip dele).
 * `unplacedRacks` — prefixos que existem no cadastro e não no desenho;
 * `onPlaceRack(prefix)` arma o traçado SÓ de desenho para um deles.
 */
const MapPalette = ({
    disabled = false,
    rackArmed = false,
    armedRack = null,
    unplacedRacks = [],
    onDragStart,
    onPickRack,
    onPlaceRack,
}) => {
    const { label } = useWarehouseLabels();

    return (
        <div className="space-y-2">
            <div className="flex flex-wrap items-center gap-2">
                <p className="text-xs font-semibold uppercase tracking-wide text-mono-500 dark:text-mono-400">
                    {label('wms.map.palette_title')}
                </p>
                {PALETTE_ITEMS.map((item) => {
                    const isRack = item.kind === 'rack';
                    const armed = isRack && rackArmed && !armedRack;
                    return (
                        <button
                            key={isRack ? 'rack' : item.type}
                            type="button"
                            disabled={disabled}
                            aria-pressed={isRack ? armed : undefined}
                            className={`${CHIP_BASE} ${armed ? CHIP_ARMED : CHIP_IDLE} ${
                                disabled
                                    ? 'cursor-not-allowed opacity-50'
                                    : isRack
                                      ? 'cursor-pointer'
                                      : 'cursor-grab touch-none active:cursor-grabbing'
                            }`}
                            onPointerDown={(event) => {
                                if (disabled || isRack || event.button > 0) {
                                    return;
                                }
                                event.preventDefault();
                                onDragStart(item, event);
                            }}
                            // Área: clique "seco" (sem arrasto) não cria nada — o
                            // hint ensina o gesto. Rack: o clique É o gesto.
                            onClick={(event) => {
                                event.preventDefault();
                                if (!disabled && isRack) {
                                    onPickRack?.();
                                }
                            }}
                        >
                            <PaletteSwatch item={item} />
                            {label(PaletteItemLabelKey(item))}
                        </button>
                    );
                })}
            </div>
            {unplacedRacks.length > 0 ? (
                <div className="flex flex-wrap items-center gap-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-warning-text dark:text-warning-300">
                        {label('wms.map.unplaced_racks')}
                    </p>
                    {unplacedRacks.map((prefix) => {
                        const armed = rackArmed && armedRack === prefix;
                        return (
                            <button
                                key={prefix}
                                type="button"
                                disabled={disabled}
                                aria-pressed={armed}
                                title={label('wms.map.unplaced_racks_hint')}
                                className={`${CHIP_BASE} min-w-0 ${armed ? CHIP_ARMED : CHIP_UNPLACED} ${
                                    disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
                                }`}
                                onClick={(event) => {
                                    event.preventDefault();
                                    if (!disabled) {
                                        onPlaceRack?.(prefix);
                                    }
                                }}
                            >
                                <PaletteSwatch item={{ kind: 'rack' }} />
                                {`${label('wms.field.rack')} ${prefix}`}
                            </button>
                        );
                    })}
                    <span className="text-xs text-mono-500 dark:text-mono-400">
                        {label('wms.map.unplaced_racks_hint')}
                    </span>
                </div>
            ) : null}
            <p className="text-xs text-mono-500 dark:text-mono-400">{label('wms.map.palette_hint')}</p>
        </div>
    );
};

export default MapPalette;
